import {reportHttpError} from '@app/service/error-reporting.service';
import {Injectable} from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import {defer, Observable, of, throwError} from 'rxjs';
import {jwtDecode} from 'jwt-decode';
import {catchError, finalize, map, shareReplay, switchMap, take, tap} from 'rxjs/operators';
import {LoginService} from '../service/login.service';
import {MessageService} from 'primeng/api';
import {LoaderRequestService} from '../service/loader-request.service';
import {Router} from '@angular/router';
import { Store } from '@ngxs/store';
import { SetAccessToken } from '@app/state/sesion/sesion.actions';
import {WorkerScheduleService} from '@app/service/worker-schedule.service';
import {MARCA} from '@app/config/plantilla';

// Clave por defecto de NgxsStoragePluginModule.forRoot() (app.module.ts), donde persiste todo el estado.
const NGXS_STORAGE_KEY = '@@STATE';
const MISMATCH_RELOAD_KEY = 'mismatchReloadAt';
const MISMATCH_RELOAD_WINDOW_MS = 30 * 1000;
const REFRESH_AHEAD_MS = 60 * 60 * 1000;
const REFRESH_AHEAD_RETRY_MS = 60 * 1000;

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private refreshToken$: Observable<string> | null = null;
  private nextProactiveRefreshAt = 0;
  private lastError: {detail: string; at: number} | null = null;

  constructor(
    public auth: LoginService,
    private messageService: MessageService,
    private loaderService: LoaderRequestService,
    private router: Router,
    private store: Store,
    private workerSchedule: WorkerScheduleService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return defer(() => {
      const authenticatedRequest = this.auth.getToken() ? this.addTokenToRequest(request) : request;
      this.showLoader(authenticatedRequest);

      const preparedRequest$ = this.shouldRefreshAhead(request)
        ? this.prepareWithFreshToken(authenticatedRequest) : of(authenticatedRequest);
      return preparedRequest$.pipe(
        switchMap(preparedRequest => defer(() => next.handle(preparedRequest)).pipe(
          catchError((errorResponse: HttpErrorResponse) => {
            if (this.workerSchedule.isCode(errorResponse, WorkerScheduleService.SESSION_ENDED)) {
              this.workerSchedule.clear();
              this.auth.logOut();
              return throwError(() => errorResponse);
            }
            if (this.workerSchedule.isCode(errorResponse, WorkerScheduleService.ACTIONS_RESTRICTED)) {
              this.workerSchedule.apply(errorResponse.error);
              return throwError(() => errorResponse);
            }
            if (errorResponse.status === 401 && this.isSessionAccountMismatch(errorResponse)) {
              this.refreshCurrentTab();
              return throwError(() => errorResponse);
            }

            // Sin sesión activa (ej. 401 de un request en vuelo tras el logout) no se renueva el token.
            if (errorResponse.status === 401 && this.auth.getLogin() && !request.url.includes('login')
                && !request.headers.get('skipAuthRefresh')) {
              return this.handle401Error(preparedRequest, next);
            }

            reportHttpError(errorResponse, request.method);
            this.handleErrorMessages(errorResponse, authenticatedRequest);
            return throwError(() => errorResponse);
          })
        )),
        // También se ejecuta al cancelar y después del reintento por renovación del token.
        finalize(() => this.hideLoader(authenticatedRequest))
      );
    });
  }

  private shouldRefreshAhead(request: HttpRequest<any>): boolean {
    if (request.headers.get('skipAuthRefresh') || /\/auth\/(login|refresh|logout)(?:[/?]|$)/.test(request.url)
        || !this.auth.getLogin() || Date.now() < this.nextProactiveRefreshAt) {
      return false;
    }
    try {
      // exp only schedules renewal; signature and authorization remain the backend's responsibility.
      const {exp} = jwtDecode<{exp?: number}>(this.auth.getLogin().accessToken);
      return typeof exp === 'number' && Number.isFinite(exp) && exp * 1000 - Date.now() <= REFRESH_AHEAD_MS;
    } catch {
      // Legacy/non-JWT tokens retain the existing 401 fallback.
      return false;
    }
  }

  private prepareWithFreshToken(request: HttpRequest<any>): Observable<HttpRequest<any>> {
    return this.getRefreshedToken().pipe(
      map(token => {
        this.nextProactiveRefreshAt = Date.now() + REFRESH_AHEAD_RETRY_MS;
        return this.addTokenToRequest(request, token);
      }),
      catchError((error: HttpErrorResponse) => {
        if (this.isSessionRejected(error) || this.isSessionAccountMismatch(error)) {
          return throwError(() => error);
        }
        // A temporary refresh outage must not block sales with a still valid access token.
        // If it is already expired, the normal 401 path remains available.
        this.nextProactiveRefreshAt = Date.now() + REFRESH_AHEAD_RETRY_MS;
        return of(request);
      })
    );
  }

  private addTokenToRequest(request: HttpRequest<any>, token: string | null = null): HttpRequest<any> {
    const authToken = token ? `Bearer ${token}` : this.auth.getToken();
    return request.clone({
      setHeaders: {
        Authorization: authToken,
        companyInfo: this.auth.getCompanyInfo()
      }
    });
  }

  private isSessionAccountMismatch(errorResponse: HttpErrorResponse): boolean {
    return errorResponse.error && errorResponse.error.code === 'SESSION_ACCOUNT_MISMATCH';
  }

  // El backend rechaza la renovación con 401 (mismatch) o 412 (ServiceException: refresh token ausente,
  // inválido o vencido, sesión invalidada). Cualquier otro status no es un rechazo de la sesión.
  private isSessionRejected(errorResponse: HttpErrorResponse): boolean {
    return errorResponse.status === 401 || errorResponse.status === 412;
  }

  private refreshCurrentTab(): void {
    this.refreshToken$ = null;
    // Otra pestaña inició sesión con otra cuenta: al recargar, esta pestaña toma esa sesión desde localStorage.
    // Si el token persistido es el mismo que el de memoria, recargar no cambia nada y la app entraría en un
    // bucle infinito de recargas (el mismatch se repite en cada arranque); en ese caso se cierra la sesión.
    if (this.hayOtraSesionPersistida() && !this.recargoRecientePorMismatch()) {
      this.marcarRecargaPorMismatch();
      this.reloadPage();
      return;
    }
    this.router.navigateByUrl('/logout');
  }

  // Separado para poder espiarlo en tests: window.location.reload no se puede mockear en Chrome.
  private reloadPage(): void {
    window.location.reload();
  }

  private hayOtraSesionPersistida(): boolean {
    try {
      const state = JSON.parse(localStorage.getItem(NGXS_STORAGE_KEY) || '{}');
      const tokenPersistido = state?.sesion?.login?.accessToken;
      const tokenEnMemoria = this.auth.getLogin()?.accessToken;
      return !!tokenPersistido && tokenPersistido !== tokenEnMemoria;
    } catch {
      return false;
    }
  }

  // Red de seguridad: como máximo una recarga por mismatch cada MISMATCH_RELOAD_WINDOW_MS por pestaña.
  private recargoRecientePorMismatch(): boolean {
    try {
      const ultimaRecarga = Number(sessionStorage.getItem(MISMATCH_RELOAD_KEY) || 0);
      return Date.now() - ultimaRecarga < MISMATCH_RELOAD_WINDOW_MS;
    } catch {
      return false;
    }
  }

  private marcarRecargaPorMismatch(): void {
    try {
      sessionStorage.setItem(MISMATCH_RELOAD_KEY, String(Date.now()));
    } catch {
      // sessionStorage no disponible: se recarga igual, hayOtraSesionPersistida ya evita el bucle.
    }
  }

  private getRefreshedToken(): Observable<string> {
    if (!this.refreshToken$) {
      this.refreshToken$ = defer(() => this.auth.refresh()).pipe(
        take(1),
        map((tokens: {accessToken: string}) => tokens.accessToken),
        tap(token => this.store.dispatch(new SetAccessToken(token))),
        catchError((errorResponse: HttpErrorResponse) => {
          if (this.isSessionAccountMismatch(errorResponse)) {
            this.refreshCurrentTab();
          } else if (this.isSessionRejected(errorResponse)) {
            this.router.navigateByUrl('/logout');
          }
          // Error de red (status 0) o del servidor (5xx): la sesión no fue rechazada, solo falló el intento.
          // Se propaga el error sin cerrar sesión; el siguiente 401 volverá a intentar renovar.
          return throwError(() => errorResponse);
        }),
        finalize(() => this.refreshToken$ = null),
        // Todos los 401 reciben el token o el error. Cancelar un GET no interrumpe la renovación compartida.
        shareReplay({bufferSize: 1, refCount: false})
      );
    }

    return this.refreshToken$;
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.getRefreshedToken().pipe(
      switchMap(token => next.handle(this.addTokenToRequest(request, token))),
      catchError((errorResponse: HttpErrorResponse) => {
        reportHttpError(errorResponse, request.method);
        this.handleErrorMessages(errorResponse, request);
        return throwError(() => errorResponse);
      })
    );
  }

  private handleErrorMessages(errorResponse: HttpErrorResponse, request: HttpRequest<any>): void {
    // Si la petición tiene el header hideErrorMessage, no mostramos el mensaje
    const hideErrorMessage = request.headers.get('hideErrorMessage');

    if (hideErrorMessage) {
      return; // No mostrar mensaje de error
    }

    // Binary downloads also receive JSON error bodies as Blob. Decode only for the toast;
    // subscribers retain the original HttpErrorResponse and binary response contract.
    if (errorResponse.error instanceof Blob) {
      const showDecodedError = (body: unknown) => this.handleErrorMessages(new HttpErrorResponse({
        error: body, status: errorResponse.status, statusText: errorResponse.statusText,
        url: errorResponse.url, headers: errorResponse.headers
      }), request);
      errorResponse.error.text().then(text => {
        let body: unknown = null;
        try { body = JSON.parse(text); } catch { /* Non-JSON download errors use the normal fallback. */ }
        showDecodedError(body);
      }, () => showDecodedError(null));
      return;
    }

    const serverMessage = errorResponse.error?.mensaje || errorResponse.error?.message;
    let detail: string;
    if (typeof serverMessage === 'string' && serverMessage.trim()) {
      detail = serverMessage;
    } else if (errorResponse.status === 0) {
      detail = `No pudimos conectar con ${MARCA}. Revisa tu conexión.`;
    } else if (errorResponse.status === 408 || errorResponse.status === 504) {
      detail = 'La solicitud tardó demasiado en responder.';
    } else if (errorResponse.status === 403) {
      detail = 'No tienes permiso para realizar esta acción.';
    } else if (errorResponse.status === 429) {
      detail = 'Hay demasiadas solicitudes. Espera un momento antes de continuar.';
    } else if (errorResponse.status >= 500) {
      detail = `${MARCA} no pudo completar la solicitud en este momento.`;
    } else {
      detail = 'No se pudo completar la solicitud. Revisa los datos e inténtalo de nuevo.';
    }
    if (errorResponse.status === 0 || errorResponse.status === 408 || errorResponse.status >= 500) {
      detail += request.method === 'GET' || request.method === 'HEAD'
        ? ' Puedes volver a cargar la información.'
        : ' Verifica si la operación se registró antes de repetirla.';
    }
    // La carga de una pantalla puede fallar en varias peticiones simultáneas.
    const repeated = this.lastError?.detail === detail && Date.now() - this.lastError.at < 3000;
    if (!repeated) {
      this.lastError = {detail, at: Date.now()};
      this.messageService.add({severity: 'error', summary: 'Error', detail});
    }
    if (errorResponse.error?.error === 'Unauthorized') {
      this.router.navigateByUrl('/logout');
    }
  }

  private showLoader(request: HttpRequest<any>): void {
    const background = request.method === 'GET';
    const hideLoader = request.headers.get('hideLoader');
    const isPrinting = request.headers.get('isPrinting');
    if (!hideLoader) {
      this.loaderService.call({
        show: true,
        background
      });
    }
    if (isPrinting) {
      this.loaderService.call({
        show: true,
        background: true
      });
    }
  }

  private hideLoader(request: HttpRequest<any>): void {
    const background = request.method === 'GET';
    const hideLoader = request.headers.get('hideLoader');
    const isPrinting = request.headers.get('isPrinting');
    if (!hideLoader) {
      this.loaderService.call({
        show: false,
        background
      });
    }
    if (isPrinting) {
      this.loaderService.call({
        show: false,
        background: true
      });
    }
  }
}
